import {Component, Input, OnDestroy, OnInit} from '@angular/core';
import {UntypedFormBuilder, UntypedFormGroup, Validators} from '@angular/forms';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {NotificationType} from '~core/enum';
import {ButtonsTranslations} from '~shared/components/buttons/buttons-translations.service';
import {SpinnerService} from '~shared/components/buttons/spinner/spinner.service';
import {FormLabelTranslationService} from '~shared/components/form-fields/translations/form-label-translations.service';
import {isStreetValid, noWhitespaceCharacters} from '~shared/directives/form-validators';
import {Address, Club, Country, Customer, GooglePlacesAddress} from '~core/models';
import {AddressService, DefaultNotificationService, StorageService} from '~core/services';
import {CustomerOverviewTranslationsService} from '~shared/translations';
import {CustomerOverViewService} from '~features/customer-overview/customer-overview.service';


/**
 * Handles contact management.
 */
@UntilDestroy()
@Component({
  selector: 'app-address',
  templateUrl: './address.component.html',
  styleUrls: ['../../../customer-overview.component.scss']
})
export class AddressComponent implements OnInit, OnDestroy {
  public countries: Array<Country> = [];
  public createAddressForm: UntypedFormGroup;
  public selectedClub: Club;
  public currentAddresses: Array<Address>;
  public spinnerName: string = 'customerAddressAdd';

  @Input() public customer: Customer;

  constructor(
    private notificationService: DefaultNotificationService,
    private formBuilder: UntypedFormBuilder,
    private addressService: AddressService,
    private customerOverviewTranslations: CustomerOverviewTranslationsService,
    private storage: StorageService,
    public flTranslations: FormLabelTranslationService,
    public customerOverviewService: CustomerOverViewService,
    public spinnerService: SpinnerService,
    public buttonsTranslations: ButtonsTranslations
  ) {
    this.storage.castCurrentCountries.pipe(untilDestroyed(this))
      .subscribe(countries => this.countries = countries);
    this.addressService.castAddressList.pipe(untilDestroyed(this))
      .subscribe(addresses => this.currentAddresses = addresses);
    this.storage.castSelectedClub.pipe(untilDestroyed(this))
      .subscribe(club => this.selectedClub = club);
  }

  /**
   * Create the form and fetch the countries and the current user
   */
  public ngOnInit(): any {
    this.fillAddressForm();
  }

  /**
   * Clear old values from the behavior subject observable.
   */
  public ngOnDestroy(): void {
  }

  /**
   * Fill the address form.
   */
  public fillAddressForm(): void {
    const defaultCountry = this.selectedClub.country !== null ? this.selectedClub.country : null;
    this.createAddressForm = this.formBuilder.group({
      customer: [`/api/customers/${this.customer.id}`],
      salutation: [null],
      firstName: [null, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(50),
        noWhitespaceCharacters
      ]],
      lastName: [null, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(200),
        noWhitespaceCharacters
      ]],
      title: [null, [
        noWhitespaceCharacters,
        Validators.maxLength(20)
      ]],
      company: [null, [
        noWhitespaceCharacters,
        Validators.maxLength(150)
      ]],
      street: [null, [
        Validators.required,
        Validators.maxLength(50),
        isStreetValid,
        noWhitespaceCharacters
      ]],
      postalCode: [null, [
        Validators.required,
        Validators.maxLength(10),
        noWhitespaceCharacters
      ]],
      city: [null, [
        Validators.required,
        Validators.maxLength(40),
        noWhitespaceCharacters
      ]],
      country: [defaultCountry, [
        Validators.required,
        Validators.minLength(2),
        Validators.maxLength(2),
        noWhitespaceCharacters
      ]],
      type: [
        'delivery',
        Validators.pattern(/^(standard|delivery|additional|)$/)
      ]
    });

    if (defaultCountry === 'DE') {
      this.createAddressForm.get('postalCode').setValidators([
        Validators.required,
        Validators.pattern(/^[0-9]{5}$/),
        noWhitespaceCharacters
      ]);
    }

    this.createAddressForm.get('country').valueChanges.pipe(untilDestroyed(this))
      .subscribe((country: string) => {
        if (country === 'DE') {
          this.createAddressForm.get('postalCode').setValidators([
            Validators.required,
            Validators.pattern(/^[0-9]{5}$/),
            noWhitespaceCharacters
          ]);
        } else {
          this.createAddressForm.get('postalCode').setValidators([
            Validators.required,
            Validators.maxLength(10),
            noWhitespaceCharacters
          ]);
        }
        this.createAddressForm.get('postalCode').updateValueAndValidity();
      });
  }

  /**
   * If address form is valid update the addresses, otherwise display errors.
   */
  public createAddressFormSubmit(form: UntypedFormGroup): void {
    if (this.createAddressForm.valid) {
      this.spinnerService.show(this.spinnerName);

      this.addressService.create(form.value).pipe(untilDestroyed(this))
        .subscribe({
          next: (data: Address) => {
            this.customerOverviewService.toggleAllModal(false);
            this.fillAddressForm();
            this.spinnerService.hide(this.spinnerName);

            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.customerOverviewTranslations.i18nSuccessAddressCreate
            });
            this.addressService.addToAddressList(data);
          },
          error: (error: Error) => {
            this.spinnerService.hide(this.spinnerName);
            this.notificationService.showNotification({
              type: NotificationType.SUCCESS,
              message: this.customerOverviewTranslations.i18nErrorAddressUpdate
            });
          }
        });
    }
  }

  /**
   * Checks for standard addresses in the array
   */
  public checkIfAddressesStandard(): any {
    return this.currentAddresses.some(el => el.type === 'standard');
  }

  /**
   * Update address values upon Google Places autocomplete address selection event.
   */
  public fillAddressValues(googlePlacesAddress: GooglePlacesAddress): void {
    this.createAddressForm.patchValue(googlePlacesAddress);
  }
}
